
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable max-classes-per-file */
import React from "react";
import moment from "moment";
import { Grid, TextField, Checkbox, ListItemText, Radio } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { CheckboxBasic } from "components/common/form";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { useSidebarFilters, RangeFilterSetting } from "../../sidebar-filters";
import { FiltersState } from "../state";
export { FilterCheckboxWithInverse, FilterCheckbox, FilterFacetCheckbox } from "./checkbox";
export { FilterRange, IncomeFilter } from "./range";

export type FilterName = keyof FiltersState;
export type FilterProps = {
  name: FilterName;
  label: string;
  filters: FilterName[];
  inputStyle?: {
    [className: string]: string;
  }
}
// Removed MenuProps because Autocomplete handles its own menu rendering

export const FilterEqualityInput: React.FC<FilterProps> = ({ inputStyle, ...props }) => {
  if (!props.filters.includes(props.name)) return null;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const value = (filterState[props.name]?.value as string) || "";

  return (
    <TextField
      style={{ ...inputStyle }}
      sx={{ maxWidth: 180 }}
      value={value}
      onChange={(e) => {
        const val = e.target.value;
        if (val) {
          addFilter(props.name, val);
        } else {
          removeFilter(props.name);
        }
      }}
      {...props}
    />
  );
};


export const FilterOver55: React.FC<FilterProps & {showForEveryone?: boolean}> = (props) => {
  if (!props.showForEveryone && !props.filters.includes(props.name)) return null;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const minYear = moment().subtract(55, "years").unix();
  const range = filterState.applicant_dob?.value as RangeFilterSetting | undefined;
  const checked = !!(range?.max && Number(range.max) === minYear);

  return (
    <CheckboxBasic
      name="applicant_dob_over_55"
      label="Applicant is 55 or older"
      labelStyle={{ marginLeft: 0, paddingLeft: 8, width: "initial" }}
      checked={checked}
      onChangeAction={(_el, { target: { checked: isChecked } }) => {
        if (isChecked) {
          addFilter("applicant_dob", `${minYear}`, "rangeMax");
        } else {
          removeFilter("applicant_dob");
        }
      }}
    />
  );
};

export const AmiRadioInput: React.FC<FilterProps & {
  value: string;
  amiRange: {
    value: string;
    min: string;
    max: string;
  }
}> = (props) => {
  const { addFilter, filterState } = useSidebarFilters();
  const checked = props.value === (filterState[props.name]?.value as string);
  return (
    <Radio
      name={props.name}
      value={props.value}
      checked={checked}
      onChange={() => {
        const { amiRange } = props;
        addFilter("household_income", { min: amiRange.min, max: amiRange.max }, "range");
        addFilter(props.name as FilterName, props.value);
      }}
      slotProps={{ input: { "aria-label": props.label as string } }}
    />
  );
};

export const FilterMultiSelect: React.FC<FilterProps & {
  options: string[];
  showForEveryone?: boolean;
}> = (props) => {
  if (!props.showForEveryone && !props.filters.includes(props.name)) return null;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const currentValue = (filterState[props.name]?.value as string[]) || [];

  return (
    <SelectAutocomplete<string, true>
      id={props.name}
      data-testid="removal-reason-select"
      multiple
      disableCloseOnSelect
      fullWidth
      label={props.label}
      options={props.options}
      value={currentValue}
      onChange={(_event: unknown, values: string[]) => {
        if (values && values.length > 0) {
          addFilter(props.name, values, "array");
        } else {
          removeFilter(props.name);
        }
      }}
      renderOption={(liProps, option: string, { selected }) => (
        <li {...liProps}>
          <Checkbox checked={selected} />
          <ListItemText
            primary={option}
            style={{ whiteSpace: "normal", wordBreak: "break-word" }}
          />
        </li>
      )}
    />
  );
};

export const FilterDateRange: React.FC<FilterProps & {
  containerStyle?: React.CSSProperties;
}> = (props) => {
  const { name } = props;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const currentRange = (filterState[name]?.value as RangeFilterSetting) || {};

  const getDisplayedValue = (isMin: boolean): Date | null => {
    const raw = isMin ? currentRange.min : currentRange.max;
    if (!raw) return null;
    // Accept previously-sent unix seconds, YYYY-MM-DD, and ISO datetimes
    if (typeof raw === "number" || (/^\d+$/).test(String(raw))) {
      return moment.unix(Number(raw)).toDate();
    }
    return moment(String(raw)).toDate();
  };

  const minVal = getDisplayedValue(true);
  const maxVal = getDisplayedValue(false);

  const handleChange = (date: Date | null, type: "min" | "max") => {
    const currentMin = type === "min" ? date : minVal;
    const currentMax = type === "max" ? date : maxVal;

    removeFilter(name);

    // Local start/end of day so "To" includes evening applicants (UTC-shifted).
    if (currentMin) {
      addFilter(name, moment(currentMin).startOf("day").toISOString(), "rangeMin");
    }

    if (currentMax) {
      addFilter(name, moment(currentMax).endOf("day").toISOString(), "rangeMax");
    }
  };

  if (!props.filters.includes(props.name as FilterName)) return null;

  return (
    <Grid container spacing={2}
      sx={props.containerStyle}>
      <Grid size={{ xs: 12 }}>
        <DatePicker
          format="MM/dd/yyyy"
          label="From"
          name="last_status_change_min"
          value={minVal}
          onChange={(date: Date | null) => handleChange(date, "min")}
          slotProps={{
            openPickerButton: { "aria-label": "change date" },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <DatePicker
          format="MM/dd/yyyy"
          label="To"
          name="last_status_change_max"
          value={maxVal}
          onChange={(date: Date | null) => handleChange(date, "max")}
          slotProps={{
            openPickerButton: { "aria-label": "change date" },
          }}
        />
      </Grid>
    </Grid>
  );
};
